import trueAxios from 'axios'

import {
  TOKEN,
  CREDENTIALS,
  DATASOURCE,
  API_BASE_LOCATION,
  GRAPHQL_API_BASE_DOMAIN,
} from '../../constants'
import MockAxios from '../../REST/mockAxios'
import {
  getLocalItem,
  setLocalItem,
  removeLocalItem,
} from '../../../local-client/local-client'

const useMock = false

let axios: any
if (useMock) {
  axios = MockAxios
} else {
  axios = trueAxios
}

function getToken(): any {
  return getLocalItem(TOKEN)
}

function setToken(token: string): boolean {
  return setLocalItem(TOKEN, token)
}

function setCredentials(credentials: string): boolean {
  return setLocalItem(CREDENTIALS, credentials)
}

function getCredentials(): any {
  return getLocalItem(CREDENTIALS)
}

function setDataSource(dataSource: DataSource): boolean {
  return setLocalItem(
    DATASOURCE,
    typeof dataSource === 'undefined' ? null : dataSource,
  )
}

function getDataSource(): any {
  return getLocalItem(DATASOURCE)
}

function removeDataSourceAndCredentials(): any {
  return removeLocalItem([CREDENTIALS, DATASOURCE])
}

function destroyToken(): boolean {
  return removeLocalItem([
    TOKEN,
    CREDENTIALS,
    DATASOURCE,
    'track-create',
    'user-session-data',
  ])
}

type DataResponse<T extends {}> = T & {
  token?: string
  message?: string
  error?: string
}

type ApiResponse<T extends {}> = T & {
  data?: DataResponse<T>
  status?: number
  message?: string
  error?: any
}

interface FormattedApiResponse<T extends {}> {
  res: {
    data?: DataResponse<T>
    status?: number
    message?: string
    error?: any
  }
  success: boolean
}

function formatResponseAndRefreshToken<T extends {}>(
  res: ApiResponse<T>,
): FormattedApiResponse<T> {
  const formattedRes = {
    success: false,
    res,
  }

  if (
    res?.error?.response?.data?.error !== 'EXPIRED_TOKEN' &&
    res?.data?.error !== 'EXPIRED_TOKEN' &&
    res?.status !== 500 &&
    res?.status !== 403
  ) {
    formattedRes.success = true
  }

  if (res?.data?.token) {
    const { token } = res.data
    setToken(token)
  }

  if (res?.data?.token) {
    const { token } = res.data
    setToken(token)
  }

  if (res?.message) {
    formattedRes.res.message = res.message
  }

  return formattedRes
}

interface GetRequest {
  endpoint: string
  token?: string
  data?: { [key: string]: any }
  options?: { [key: string]: any }
}

interface Headers {
  [key: string]: any
}

async function makeGet(
  { endpoint, data, options }: GetRequest,
  type = '',
  graphQL = false,
) {
  const token = getToken()
  let headers: Headers = {}
  if (options) headers = { ...headers, ...options }

  if (
    process.env.REACT_APP_STAGING_OVERRIDE === 'true' &&
    process.env.REACT_APP_NODE_ENV !== 'production'
  ) {
    headers['override-staging-database'] = 'true'
  }

  if (token) {
    if (graphQL) {
      headers.Authorization = `Bearer ${token}`
    } else {
      headers.Authorization = token
    }
  }

  try {
    const res = await axios({
      ...data,
      ...options,
      method: 'get',
      url: `${
        !graphQL ? API_BASE_LOCATION : GRAPHQL_API_BASE_DOMAIN
      }${type}${endpoint}`,
      headers,
    })

    return res
  } catch (e) {
    return {
      error: e,
      // @ts-ignore
      status: e?.response?.status || 500,
    }
  }
}

interface PostRequest {
  endpoint: string
  token?: string
  data?: { [key: string]: any }
  options?: { [key: string]: any }
}

async function makePost(
  { endpoint, data = {}, options = {} }: PostRequest,
  type = '',
  graphQL = false,
): Promise<any> {
  const token = getToken()
  let headers: Headers = {}

  if (options) headers = { ...headers, ...options }

  if (
    process.env.REACT_APP_STAGING_OVERRIDE === 'true' &&
    process.env.REACT_APP_NODE_ENV !== 'production'
  ) {
    headers['override-staging-database'] = 'true'
  }

  if (token) {
    if (graphQL) {
      headers.Authorization = `Bearer ${token}`
    } else {
      headers.Authorization = token
    }
  }
  try {
    const res = await axios.post(
      `${
        !graphQL ? API_BASE_LOCATION : GRAPHQL_API_BASE_DOMAIN
      }${type}${endpoint}`,
      data,
      {
        ...options,
        headers,
      },
    )

    return res
  } catch (e) {
    return {
      // @ts-ignore
      status: e?.response?.status || 500,
      // @ts-ignore
      message: e?.response?.data?.message,
      error: e,
    }
  }
}

interface DeleteRequest {
  endpoint: string
  token?: string
  data?: { [key: string]: any }
  options?: { [key: string]: any }
}

async function makeDelete(
  { endpoint, options = {}, data = {} }: DeleteRequest,
  type = '',
) {
  const token = getToken()
  let headers: Headers = {}

  if (options) headers = { ...headers, ...options }
  if (token) headers.Authorization = token

  try {
    const res = await axios.delete(`${API_BASE_LOCATION}${type}${endpoint}`, {
      ...options,
      headers,
      data,
    })

    return res
  } catch (e) {
    return {
      status: 500,
      error: e,
    }
  }
}

interface PutRequest {
  endpoint: string
  token?: string
  data?: { [key: string]: any }
  options?: { [key: string]: any }
}

async function makePut(
  { endpoint, data = {}, options = {} }: PutRequest,
  type = '',
) {
  const token = getToken()
  let headers: Headers = {}

  if (options) headers = { ...headers, ...options }
  if (token) headers.Authorization = token

  try {
    const res = await axios.put(
      `${API_BASE_LOCATION}${type}${endpoint}`,
      data,
      {
        ...options,
        headers,
      },
    )

    return res
  } catch (e) {
    return {
      error: e,
      status: 500,
    }
  }
}

function getEndpoint(type: string, endpointEnding: string, graphql = false) {
  return `${
    graphql ? GRAPHQL_API_BASE_DOMAIN : API_BASE_LOCATION
  }${type}${endpointEnding}`
}

// Progressively moving to use this module rather than api-client
export default function Make(type = '', graphQL = false) {
  return {
    get: async <T extends {}>(all: GetRequest) =>
      formatResponseAndRefreshToken<T>(
        await makeGet({ ...all }, type, graphQL),
      ),
    post: async <T extends {}>(all: PostRequest) =>
      formatResponseAndRefreshToken<T>(
        await makePost({ ...all }, type, graphQL),
      ),
    put: async <T extends {}>(all: PutRequest) =>
      formatResponseAndRefreshToken<T>(await makePut({ ...all }, type)),
    delete: async <T extends {}>(all: DeleteRequest) =>
      formatResponseAndRefreshToken<T>(await makeDelete({ ...all }, type)),
    getEndpoint: (endpointEnding: string, graphql = false) =>
      getEndpoint(type, endpointEnding, graphql),
    destroyToken,
    getToken,
    setToken,
    setCredentials,
    getCredentials,
    setDataSource,
    getDataSource,
    removeDataSourceAndCredentials,
  }
}
